import { useEffect, useRef } from 'react'
import { geoOrthographic, geoPath, geoGraticule10, geoDistance, geoContains } from 'd3-geo'
import { feature } from 'topojson-client'
import landTopo from 'world-atlas/land-110m.json'

/* ---- wireframe dotted globe, in the spirit of 21st.dev's
   "wireframe-dotted-globe" (RotatingEarth) component — a d3-geo orthographic
   projection rendered to a <canvas>: a graticule wireframe, a dotted
   land-mass texture, and a slow auto-rotation. Built directly on d3-geo
   (the package that component itself depends on) since its exact source
   wasn't fetchable, using world-atlas's land-110m topology for the coastlines. ---- */

const land = feature(landTopo, landTopo.objects.land)
const graticule = geoGraticule10()

// Precompute a lat/lng dot grid once, thinned near the poles so longitude
// lines don't cluster, keeping only points that fall on land.
const DOT_LAT_STEP = 3.2
const LAND_DOTS = []
for (let lat = -84; lat <= 84; lat += DOT_LAT_STEP) {
  const lngStep = Math.min(DOT_LAT_STEP / Math.max(Math.cos((lat * Math.PI) / 180), 0.15), 12)
  for (let lng = -180; lng < 180; lng += lngStep) {
    LAND_DOTS.push([lng, lat])
  }
}
const VISIBLE_LAND_DOTS = LAND_DOTS.filter((p) => geoContains(land, p))

export default function WireframeGlobe({ className = '' }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const ctx = canvas.getContext('2d')

    let cancelled = false
    let frameId = 0
    let rotation = 0
    let size = 0

    function resize() {
      size = canvas.clientWidth
      const dpr = Math.min(window.devicePixelRatio, 2)
      canvas.width = size * dpr
      canvas.height = size * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(canvas)

    function render() {
      if (cancelled) return
      const phi = -18
      const radius = (size / 2) * 0.9
      const projection = geoOrthographic().scale(radius).translate([size / 2, size / 2]).rotate([rotation, phi])
      const path = geoPath(projection, ctx)
      const center = [-rotation, -phi]

      ctx.clearRect(0, 0, size, size)

      ctx.beginPath()
      path({ type: 'Sphere' })
      ctx.strokeStyle = 'rgba(245,245,243,.16)'
      ctx.lineWidth = 1
      ctx.stroke()

      ctx.beginPath()
      path(graticule)
      ctx.strokeStyle = 'rgba(245,245,243,.12)'
      ctx.lineWidth = 0.6
      ctx.stroke()

      ctx.fillStyle = 'rgba(245,245,243,.85)'
      for (const point of VISIBLE_LAND_DOTS) {
        if (geoDistance(point, center) > Math.PI / 2) continue
        const p = projection(point)
        if (!p) continue
        ctx.beginPath()
        ctx.arc(p[0], p[1], size * 0.0021, 0, Math.PI * 2)
        ctx.fill()
      }

      rotation += 0.12
      frameId = requestAnimationFrame(render)
    }
    frameId = requestAnimationFrame(render)

    return () => {
      cancelled = true
      resizeObserver.disconnect()
      cancelAnimationFrame(frameId)
    }
  }, [])

  return <canvas ref={canvasRef} className={`block aspect-square w-full max-w-[480px] ${className}`} />
}
